// Admin-Dashboard — Liste aller Surveys, die in diesem Browser angelegt
// wurden. Privacy-First: der Server weiss nichts davon, alle Refs sitzen
// im localStorage. Master-File-Import zum Übernehmen aus einem anderen
// Browser/Gerät.

import { listSurveys, addSurvey, removeSurvey, mergeSurveys, type AdminSurveyRef } from '../admin-store';
import { renderAppHeader } from './app-header';
import { el, clear } from './dom';

export function renderDashboardView(root: HTMLElement): void {
  const container = el('div', { class: 'dashboard-view' });
  root.append(renderAppHeader(), container);
  render();

  function render(): void {
    clear(container);
    const surveys = listSurveys()
      .filter((s) => !s.hidden)
      .sort((a, b) => b.createdAt - a.createdAt);

    container.append(
      el('header', { class: 'dashboard-header' },
        el('h1', { class: 'dashboard-title' }, 'Meine Umfragen'),
        el('p', { class: 'dashboard-intro' },
          'Alle Slot-Umfragen, die du in diesem Browser angelegt hast. ',
          el('strong', {}, 'Diese Liste lebt nur lokal'),
          ' — Slot-Server weiss nichts davon. Wenn du den Browser-Cache löschst, ist sie weg (deine Privacy-Dateien sind dann der Backup).',
        ),
      ),
    );

    if (surveys.length === 0) {
      container.append(
        el('section', { class: 'dashboard-empty' },
          el('p', {}, 'Noch keine Umfragen in diesem Browser.'),
          el('div', { class: 'dashboard-cta-row' },
            el('a', { href: '/new', class: 'btn-primary' }, '+ Neue Umfrage anlegen'),
            renderImportButton(render),
          ),
        ),
      );
      return;
    }

    container.append(renderToolbar(render));
    const list = el('ul', { class: 'dashboard-list' });
    for (const s of surveys) list.append(renderItem(s, render));
    container.append(list);
    container.append(renderImportSection(render));
  }
}

function renderToolbar(rerender: () => void): HTMLElement {
  const bar = el('section', { class: 'dashboard-toolbar' });
  const newBtn = el('a', { href: '/new', class: 'btn-primary' }, '+ Neue Umfrage');
  const exportBtn = el('button', { type: 'button', class: 'btn-secondary' }, '⬇ Master-Datei sichern');
  exportBtn.addEventListener('click', () => downloadMasterFile());
  const importBtn = renderImportButton(rerender);
  bar.append(newBtn, exportBtn, importBtn);
  return bar;
}

function renderItem(s: AdminSurveyRef, rerender: () => void): HTMLElement {
  const item = el('li', { class: 'dashboard-item' });
  const titleText = s.title?.trim() || 'Ohne Titel';
  const created = formatDate(s.createdAt);
  const expires = s.expiresAt === null
    ? 'läuft nicht ab'
    : `läuft ab ${formatDate(s.expiresAt)}`;

  const head = el('div', { class: 'dashboard-item-head' });
  head.append(
    el('a', { class: 'dashboard-item-title', href: `/admin/${s.adminToken}` }, titleText),
    el('span', { class: 'dashboard-item-meta' }, `${created} · ${expires}`),
  );

  const actions = el('div', { class: 'dashboard-item-actions' });
  const adminLink = el('a',
    { href: `/admin/${s.adminToken}`, class: 'btn-secondary dashboard-item-btn' },
    'Auswertung →',
  );
  const voterCopy = el('button',
    { type: 'button', class: 'btn-secondary dashboard-item-btn', title: 'Voter-Link in Zwischenablage' },
    '🔗 Voter-Link',
  );
  voterCopy.addEventListener('click', async (e) => {
    e.preventDefault();
    const url = `${window.location.origin}/vote/${s.voterToken}`;
    try {
      await navigator.clipboard.writeText(url);
      voterCopy.textContent = '✓ Kopiert';
      setTimeout(() => { voterCopy.textContent = '🔗 Voter-Link'; }, 1500);
    } catch {
      window.prompt('Voter-Link:', url);
    }
  });
  const removeBtn = el('button',
    { type: 'button', class: 'btn-secondary dashboard-item-btn dashboard-item-remove', title: 'Aus dieser Liste entfernen' },
    '× Aus Liste',
  );
  removeBtn.addEventListener('click', () => {
    const ok = window.confirm(
      `„${titleText}" aus dieser Liste entfernen?\n\n` +
      'Die Umfrage selbst bleibt am Server bestehen — du verlierst nur die Verknüpfung in diesem Browser. ' +
      'Der Admin-Link aus der Privacy-Datei funktioniert weiter.',
    );
    if (ok) {
      removeSurvey(s.adminToken);
      rerender();
    }
  });
  actions.append(adminLink, voterCopy, removeBtn);

  item.append(head, actions);
  return item;
}

function renderImportSection(rerender: () => void): HTMLElement {
  const sec = el('section', { class: 'dashboard-import-section' });
  sec.append(
    el('h3', { class: 'dashboard-import-heading' }, 'Master-Datei importieren'),
    el('p', { class: 'dashboard-import-hint' },
      'Eine Slot-Master-Datei (.html) hierher ziehen, um Umfragen aus einem anderen Browser zu übernehmen. ',
      'Privacy-File einer einzelnen Umfrage geht auch.',
    ),
  );
  const drop = el('div', { class: 'dashboard-drop', tabindex: '0', role: 'button' });
  drop.append(el('span', {}, '🗂️  Datei hier ablegen oder klicken'));
  const input = el('input', { type: 'file', accept: '.html,text/html', style: 'display:none' }) as HTMLInputElement;
  drop.append(input);
  drop.addEventListener('click', () => input.click());

  const stop = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
  for (const ev of ['dragenter', 'dragover'] as const) {
    drop.addEventListener(ev, (e) => { stop(e); drop.classList.add('is-dragging'); });
  }
  for (const ev of ['dragleave', 'dragend'] as const) {
    drop.addEventListener(ev, (e) => { stop(e); drop.classList.remove('is-dragging'); });
  }
  drop.addEventListener('drop', async (e) => {
    stop(e);
    drop.classList.remove('is-dragging');
    const f = e.dataTransfer?.files?.[0];
    if (f) await handleFile(f, drop, rerender);
  });
  input.addEventListener('change', async () => {
    const f = input.files?.[0];
    if (f) await handleFile(f, drop, rerender);
    input.value = '';
  });

  sec.append(drop);
  return sec;
}

function renderImportButton(rerender: () => void): HTMLElement {
  const btn = el('label',
    { class: 'btn-secondary', style: 'cursor: pointer; display: inline-flex; align-items: center; gap: 0.4rem' },
    '⬆ Master-Datei laden',
  );
  const input = el('input', { type: 'file', accept: '.html,text/html', style: 'display:none' }) as HTMLInputElement;
  input.addEventListener('change', async () => {
    const f = input.files?.[0];
    if (f) await handleFile(f, btn, rerender);
    input.value = '';
  });
  btn.append(input);
  return btn;
}

async function handleFile(file: File, statusEl: HTMLElement, rerender: () => void): Promise<void> {
  if (file.size > 4 * 1024 * 1024) {
    flashError(statusEl, 'Datei zu groß (>4 MB).');
    return;
  }
  let text: string;
  try { text = await file.text(); }
  catch { flashError(statusEl, 'Datei konnte nicht gelesen werden.'); return; }

  const refs = extractRefsFromHtml(text);
  if (refs.length === 0) {
    flashError(statusEl, 'Keine Slot-Tokens in dieser Datei gefunden.');
    return;
  }
  const result = mergeSurveys(refs);
  if (result.added > 0) {
    flashSuccess(statusEl,
      `${result.added} neue Umfrage${result.added === 1 ? '' : 'n'} importiert${result.existing > 0 ? `, ${result.existing} schon vorhanden` : ''}.`,
    );
    rerender();
  } else {
    flashSuccess(statusEl, 'Alle Umfragen waren schon in deiner Liste.');
  }
}

function flashSuccess(el2: HTMLElement, msg: string): void {
  const orig = el2.textContent;
  el2.textContent = '✓ ' + msg;
  setTimeout(() => { el2.textContent = orig; }, 2500);
}

function flashError(el2: HTMLElement, msg: string): void {
  const orig = el2.textContent;
  el2.textContent = '✗ ' + msg;
  setTimeout(() => { el2.textContent = orig; }, 3000);
}

/** Sucht im HTML nach <script type="application/json" id="slot-refs">...</script>
 *  ODER nach einem einzelnen Slot-Privacy-File-Embed (slot-privacy-file). */
function extractRefsFromHtml(html: string): AdminSurveyRef[] {
  const refs: AdminSurveyRef[] = [];

  // Master-File: mehrere Refs als JSON-Array
  const masterMatch = html.match(/<script[^>]*id=["']slot-master["'][^>]*>([\s\S]*?)<\/script>/i);
  if (masterMatch) {
    try {
      const data = JSON.parse(masterMatch[1].trim());
      if (Array.isArray(data?.surveys)) {
        for (const r of data.surveys) {
          const ref = coerceRef(r);
          if (ref) refs.push(ref);
        }
      }
    } catch { /* fall through */ }
  }

  // Single Privacy-File
  const single = html.match(/<script[^>]*type=["']application\/json["'][^>]*>\s*(\{[\s\S]*?"slot-privacy-file"[\s\S]*?\})\s*<\/script>/i);
  if (single) {
    try {
      const data = JSON.parse(single[1]);
      const ref = coerceRef({
        id: data.surveyId,
        title: data.title,
        adminToken: data.adminToken,
        voterToken: data.voterToken,
        createdAt: data.createdAt,
        expiresAt: data.expiresAt ?? null,
      });
      if (ref) refs.push(ref);
    } catch { /* ignore */ }
  }

  return refs;
}

function coerceRef(x: unknown): AdminSurveyRef | null {
  if (typeof x !== 'object' || x === null) return null;
  const r = x as Record<string, unknown>;
  if (typeof r.adminToken !== 'string' || !/^[a-f0-9]{32}$/.test(r.adminToken)) return null;
  if (typeof r.voterToken !== 'string' || !/^[a-f0-9]{32}$/.test(r.voterToken)) return null;
  if (typeof r.id !== 'string') return null;
  return {
    id: r.id,
    title: typeof r.title === 'string' ? r.title : null,
    adminToken: r.adminToken,
    voterToken: r.voterToken,
    createdAt: typeof r.createdAt === 'number' ? r.createdAt : Math.floor(Date.now() / 1000),
    expiresAt: typeof r.expiresAt === 'number' ? r.expiresAt : null,
  };
}

/** Erzeugt + lädt eine Master-Datei aller im localStorage gespeicherten Surveys. */
function downloadMasterFile(): void {
  const surveys = listSurveys();
  const data = {
    version: 1,
    kind: 'slot-master-file',
    exportedAt: Math.floor(Date.now() / 1000),
    surveys,
  };
  const json = JSON.stringify(data, null, 2);
  const html = `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>Slot — Meine Umfragen</title>
<style>
  body { font: 16px/1.5 -apple-system, system-ui, sans-serif; color: #1a2233; background: #faf6ed;
         max-width: 38rem; margin: 3rem auto; padding: 0 1rem; }
  h1 { font: 400 1.6rem/1.2 Georgia, serif; margin: 0 0 0.5rem; }
  h1 em { color: #c4574a; font-style: italic; }
  ul { padding-left: 1.2rem; }
  li { margin: 0.4rem 0; }
  a { color: #1a2233; }
  a:hover { color: #c4574a; }
  .privacy { background: rgba(47,158,86,0.1); border-radius: 6px;
             padding: 0.7rem 1rem; margin-top: 2rem; font-size: 0.85rem; }
</style>
</head>
<body>
<h1>Slot — <em>Meine Umfragen</em></h1>
<p>${surveys.length} Umfrage${surveys.length === 1 ? '' : 'n'}, exportiert ${formatDate(Math.floor(Date.now() / 1000))}.</p>
<ul>
${surveys.map((s) => `  <li>${escapeHtml(s.title ?? 'Ohne Titel')} — <a href="https://slot.gotyou.at/admin/${s.adminToken}">Admin</a> · <a href="https://slot.gotyou.at/vote/${s.voterToken}">Voter</a></li>`).join('\n')}
</ul>
<p class="privacy">🔒 Diese Datei enthält deine Admin-Zugänge. <strong>Behalte sie für dich.</strong> Wer sie hat, hat Vollzugriff auf die Auswertungen. Doppelklick auf <em>Slot-Dashboard ↗</em> → die Umfragen wandern zurück in den Browser.</p>
<p><a href="https://slot.gotyou.at/admin">↗ Zum Slot-Dashboard (zum Importieren auf neuem Gerät)</a></p>
<script type="application/json" id="slot-master">
${json}
</script>
</body>
</html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `slot-master-${new Date().toISOString().slice(0, 10)}.html`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function formatDate(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Re-export Helper damit share-panel.ts die addSurvey-Logik nutzt
export { addSurvey };
